(function () {
  'use strict';

  const MOBILE_MAX = 767;
  const TABLET_MAX = 989;

  function needsReadMore(excerpt) {
    if (!excerpt) return false;
    return excerpt.scrollHeight > excerpt.clientHeight + 1;
  }

  function updateReadMore(card) {
    const excerpt = card.querySelector('[data-review-excerpt]');
    const button = card.querySelector('[data-review-read-more]');

    if (!excerpt || !button) return;

    const shouldShow = needsReadMore(excerpt);
    button.classList.toggle('is-hidden', !shouldShow);
    button.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    button.tabIndex = shouldShow ? 0 : -1;
  }

  function updateAllReadMore(cards) {
    cards.forEach(updateReadMore);
  }

  if (customElements.get('trustpilot-reviews')) return;

  customElements.define(
    'trustpilot-reviews',
    class TrustpilotReviews extends HTMLElement {
      constructor() {
        super();
        this.onResize = this.onResize.bind(this);
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onTrackScroll = this.onTrackScroll.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('[data-trustpilot-track]');
        this.nav = this.querySelector('[data-trustpilot-nav]');
        this.prevBtn = this.querySelector('[data-trustpilot-prev]');
        this.nextBtn = this.querySelector('[data-trustpilot-next]');
        this.cards = [...this.querySelectorAll('[data-trustpilot-card]')];

        this.cards.forEach((card) => updateReadMore(card));

        this.prevBtn?.addEventListener('click', this.onPrevClick);
        this.nextBtn?.addEventListener('click', this.onNextClick);
        this.track?.addEventListener('scroll', this.onTrackScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        if (this.track && typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(() => {
            this.updateLayout();
            this.scheduleReadMoreCheck();
          });
          this.resizeObserver.observe(this.track);
        }

        this.updateLayout();
        this.updateNavState();
        this.scheduleReadMoreCheck();

        if (document.fonts?.ready) {
          document.fonts.ready.then(() => this.scheduleReadMoreCheck());
        }
      }

      scheduleReadMoreCheck() {
        cancelAnimationFrame(this.readMoreFrame);
        this.readMoreFrame = requestAnimationFrame(() => {
          requestAnimationFrame(() => updateAllReadMore(this.cards));
        });
      }

      disconnectedCallback() {
        this.prevBtn?.removeEventListener('click', this.onPrevClick);
        this.nextBtn?.removeEventListener('click', this.onNextClick);
        this.track?.removeEventListener('scroll', this.onTrackScroll);
        window.removeEventListener('resize', this.onResize);
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.readMoreFrame);
      }

      isSliderActive() {
        const width = window.innerWidth;

        if (width <= MOBILE_MAX) {
          return this.dataset.sliderMobile === 'true';
        }

        if (width <= TABLET_MAX) {
          return this.dataset.sliderTablet === 'true';
        }

        return this.dataset.sliderDesktop === 'true';
      }

      getItemsVisible() {
        const width = window.innerWidth;

        if (width <= MOBILE_MAX) {
          return parseFloat(this.dataset.itemsMobile) || 1.2;
        }

        if (width <= TABLET_MAX) {
          return parseFloat(this.dataset.itemsTablet) || 2.5;
        }

        return parseFloat(this.dataset.itemsDesktop) || 4;
      }

      updateLayout() {
        if (!this.track) return;

        if (!this.isSliderActive()) {
          this.track.style.removeProperty('--trustpilot-card-width');
          if (this.nav) this.nav.hidden = true;
          return;
        }

        const trackWidth = this.track.clientWidth;
        if (!trackWidth) return;

        const itemsVisible = this.getItemsVisible();
        const gap =
          parseFloat(getComputedStyle(this.track).gap) ||
          parseFloat(getComputedStyle(this).getPropertyValue('--trustpilot-gap')) ||
          16;
        const cardWidth = (trackWidth - gap * (itemsVisible - 1)) / itemsVisible;

        this.track.style.setProperty('--trustpilot-card-width', `${Math.max(cardWidth, 220)}px`);

        if (this.nav) this.nav.hidden = false;
        this.updateNavState();
      }

      updateNavState() {
        if (!this.track || !this.prevBtn || !this.nextBtn || !this.isSliderActive()) return;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const scrollLeft = this.track.scrollLeft;

        this.prevBtn.disabled = scrollLeft <= 1;
        this.nextBtn.disabled = scrollLeft >= maxScroll - 1;
      }

      onTrackScroll() {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => this.updateNavState(), 80);
      }

      onResize() {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.updateLayout();
          this.scheduleReadMoreCheck();
        }, 150);
      }

      onPrevClick() {
        this.advance(-1);
      }

      onNextClick() {
        this.advance(1);
      }

      advance(direction) {
        if (!this.track || !this.isSliderActive()) return;

        const card = this.track.querySelector('[data-trustpilot-card]');
        if (!card) return;

        const gap = parseFloat(getComputedStyle(this.track).gap) || 16;
        const amount = (card.offsetWidth + gap) * direction;

        this.track.scrollBy({
          left: amount,
          behavior: 'smooth',
        });
      }
    }
  );
})();
